import { parseISOString } from './parseISOString'

/**
 * An ISO date-time as `YYYY-MM-DD  -  HH:MM`.
 *
 * Extracted from plugins/formatDateTimeString.ts, which now delegates here --
 * the last of the three date formatters to make that move (its `…DateOnly` and
 * `…TimeOnly` siblings already had). A pure module is what a data object can
 * call: `$formatDateTimeString` is a Vue global property and is reachable only
 * from a component.
 *
 * ⚠ Two pieces of the contract are relied on by live call sites and must not be
 * tidied:
 *
 *  - The null/empty answers differ. `null` in returns `null`; any other falsey
 *    value returns `undefined`. Callers render both as an empty cell, so the
 *    distinction has never mattered visually -- but it is what the four header
 *    definitions using this have always seen.
 *  - The separator is two spaces, a hyphen, two spaces.
 *
 * ⚠ The two halves disagree about time zone, deliberately left alone.
 * `parseISOString` reinterprets the string's wall-clock digits AS UTC, so
 * `toISOString()` gives back the literal date digits regardless of where the
 * reader is, while `toLocaleTimeString` renders in their local zone. Near
 * midnight the date and the time can therefore belong to different days. That
 * is pre-existing behaviour across every consumer; changing it here would move
 * four columns at once.
 */
export function formatDateTimeString(value: string | null) {
  if (value === null) {
    return value
  }

  if (!value) {
    return undefined
  }

  // Parsed once rather than the original's twice; `parseISOString` is pure, so
  // the result is identical.
  const parsed = parseISOString(value)
  return (
    parsed.toISOString().substring(0, 10) +
    '  -  ' +
    parsed.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })
  )
}
